"use client";

import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { commonApiFetch, commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useWaveEligibility } from "../WaveEligibilityContext";
import type { WaveDataStoreUpdater } from "./types";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { WebSocketStatus } from "@/services/websocket/WebSocketTypes";
import { recordReactionRealtimeReconciliation } from "@/utils/monitoring/dropReactionMonitoring";
interface UseWaveRealtimeUpdaterProps extends WaveDataStoreUpdater {
  readonly activeWaveId: string | null;
  readonly registerWave: (waveId: string) => void;
  readonly syncNewestMessages: (waveId: string, sinceSerialNo: number, signal: AbortSignal) => Promise<{
    drops: ApiDrop[] | null;
    highestSerialNo: number | null;
  }>;
  readonly removeWaveDeliveredNotifications: (waveId: string) => Promise<void>;
  readonly isWaveMuted: (waveId: string) => boolean;
}
export enum ProcessIncomingDropType {
  DROP_RATING_UPDATE = "DROP_RATING_UPDATE",
  DROP_INSERT = "DROP_INSERT",
  DROP_REACTION_UPDATE = "DROP_REACTION_UPDATE",
}
type ProcessIncomingDropFn = (dropData: ApiDrop, type: ProcessIncomingDropType) => void;
export function useWaveRealtimeUpdater({
  activeWaveId,
  getData,
  updateData,
  registerWave,
  syncNewestMessages,
  removeDrop,
  removeWaveDeliveredNotifications,
  isWaveMuted
}: UseWaveRealtimeUpdaterProps): {
  processIncomingDrop: ProcessIncomingDropFn;
  processDropRemoved: (waveId: string, dropId: string) => void;
} {
  const isFetchingNewestRef = useRef<Record<string, boolean>>({});
  const needsRefetchAfterCurrentRef = useRef<Record<string, boolean>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const {
    refreshEligibility
  } = useWaveEligibility();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  const tabJustBecameVisibleRef = useRef<boolean>(false);

  // Function to cleanup abort controllers
  const cleanupController = useCallback((waveId: string) => {
    if (abortControllersRef.current[waveId]) {
      delete abortControllersRef.current[waveId];
    }
  }, []);

  // Function to initiate the fetch newest cycle
  const initiateFetchNewestCycle = useCallback(async (waveId_0: string, sinceSerialNo: number) => {
    if (isFetchingNewestRef.current[waveId_0]) {
      needsRefetchAfterCurrentRef.current[waveId_0] = true;
      return;
    }
    isFetchingNewestRef.current[waveId_0] = true;
    const controller = new AbortController();
    abortControllersRef.current[waveId_0] = controller;
    try {
      const {
        drops: fetchedDrops,
        highestSerialNo: fetchedHighestSerial
      } = await syncNewestMessages(waveId_0, sinceSerialNo, controller.signal);
      if (fetchedDrops) {
        const currentData = getData(waveId_0);
        if (currentData) {
          const newDrops: ExtendedDrop[] = fetchedDrops.map(drop => ({
            ...drop,
            type: DropSize.FULL,
            stableKey: drop.id,
            // Assuming ApiDrop has id
            stableHash: drop.id // Assuming ApiDrop has id
          }));
          updateData({
            key: waveId_0,
            drops: newDrops,
            // Update latestFetchedSerialNo only if the fetch returned drops
            latestFetchedSerialNo: fetchedHighestSerial !== null ? fetchedHighestSerial : null
            // Optionally reset hasNextPage if needed, though fetchNewest shouldn't affect it
          });
        }
      }
    } catch (error) {
      // Do not update latestFetchedSerialNo on error
      if (error instanceof DOMException && error.name === "AbortError") {
        // Fetch was cancelled - this is expected behavior
      } else {
        console.error("Error fetching newest messages:", error);
      }
    } finally {
      cleanupController(waveId_0);
      isFetchingNewestRef.current[waveId_0] = false;
      if (needsRefetchAfterCurrentRef.current[waveId_0]) {
        needsRefetchAfterCurrentRef.current[waveId_0] = false;
        const latestData = getData(waveId_0);
        if (latestData?.latestFetchedSerialNo) {
          await initiateFetchNewestCycle(waveId_0, latestData.latestFetchedSerialNo);
        }
      }
    }
  }, [getData, updateData, syncNewestMessages, cleanupController]);

  // WebSocket message handler
  const processIncomingDrop: ProcessIncomingDropFn = useCallback(async (drop_0: ApiDrop, type: ProcessIncomingDropType) => {
    const markWaveAsRead = async (waveId_1: string) => {
      await commonApiPostWithoutBodyAndResponse({
        endpoint: `notifications/wave/${waveId_1}/read`
      });
      invalidateNotifications();
    };
    if (!drop_0?.wave?.id) {
      return;
    }
    const waveId_2 = drop_0.wave.id;
    if (isWaveMuted(waveId_2)) {
      return;
    }

    // Check if tab just became visible and refresh eligibility
    if (tabJustBecameVisibleRef.current) {
      tabJustBecameVisibleRef.current = false;
      refreshEligibility(waveId_2);
    }
    const currentData_0 = getData(waveId_2);
    if (!currentData_0) {
      // Wave not registered or data not loaded yet.
      // Registering will trigger initial fetch which should get this message.
      registerWave(waveId_2);
      return;
    }
    const existingDrop = currentData_0.drops.find(d => d.id === drop_0.id);
    if (type === ProcessIncomingDropType.DROP_RATING_UPDATE && !existingDrop || type === ProcessIncomingDropType.DROP_REACTION_UPDATE && !existingDrop || existingDrop?.type === DropSize.LIGHT) {
      return;
    }
    if ((type === ProcessIncomingDropType.DROP_RATING_UPDATE || type === ProcessIncomingDropType.DROP_REACTION_UPDATE) && existingDrop) {
      const apiDrop = await commonApiFetch<ApiDrop>({
        endpoint: `drops/${drop_0.id}`
      });
      if (apiDrop) {
        if (type === ProcessIncomingDropType.DROP_REACTION_UPDATE) {
          recordReactionRealtimeReconciliation({
            drop: {
              id: apiDrop.id,
              wave: {
                id: apiDrop.wave.id
              },
              context_profile_context: apiDrop.context_profile_context
            },
            websocketStatus: WebSocketStatus.CONNECTED
          });
        }
        updateData({
          key: waveId_2,
          drops: [{
            ...apiDrop,
            type: DropSize.FULL,
            stableHash: existingDrop.stableHash,
            stableKey: existingDrop.stableKey
          }]
        });
      }
      return;
    }
    const optimisticDrop: ExtendedDrop = {
      ...drop_0,
      type: DropSize.FULL,
      author: {
        ...drop_0.author,
        subscribed_actions: existingDrop ? existingDrop.author.subscribed_actions : drop_0.author.subscribed_actions ?? []
      },
      wave: {
        ...drop_0.wave,
        authenticated_user_eligible_to_participate: existingDrop ? existingDrop.wave.authenticated_user_eligible_to_participate : drop_0.wave.authenticated_user_eligible_to_participate ?? false,
        authenticated_user_eligible_to_vote: existingDrop ? existingDrop.wave.authenticated_user_eligible_to_vote : drop_0.wave.authenticated_user_eligible_to_vote ?? false,
        authenticated_user_eligible_to_chat: existingDrop ? existingDrop.wave.authenticated_user_eligible_to_chat : drop_0.wave.authenticated_user_eligible_to_chat ?? false,
        authenticated_user_admin: existingDrop ? existingDrop.wave.authenticated_user_admin : drop_0.wave.authenticated_user_admin ?? false
      },
      // Assuming message structure matches ApiDrop + ApiWaveMin
      stableKey: drop_0.id,
      stableHash: drop_0.id,
      // Use ID for hash temporarily
      context_profile_context: existingDrop ? existingDrop.context_profile_context : drop_0.context_profile_context ?? null
    };

    // Important: Identify the serial number *before* adding the optimistic drop
    const serialNoForFetch = currentData_0.latestFetchedSerialNo;
    updateData({
      key: waveId_2,
      drops: [optimisticDrop]
    });
    if (serialNoForFetch && !optimisticDrop.id.startsWith("temp-")) {
      // Initiate the background fetch for reconciliation
      initiateFetchNewestCycle(waveId_2, serialNoForFetch);
    }
    if (activeWaveId === waveId_2) {
      removeWaveDeliveredNotifications(waveId_2).catch(error_0 => console.error("Failed to remove wave delivered notifications:", error_0));
      markWaveAsRead(waveId_2).catch(error_1 => console.error("Failed to mark wave as read:", error_1));
    }
  }, [activeWaveId, getData, updateData, registerWave, initiateFetchNewestCycle, removeWaveDeliveredNotifications, refreshEligibility, isWaveMuted, invalidateNotifications]);
  const processDropRemoved = useCallback((waveId_3: string, dropId: string) => {
    removeDrop(waveId_3, dropId);
  }, [removeDrop]);
  useWebSocketMessage<WsDropUpdateMessage["data"]>(WsMessageType.DROP_UPDATE, messageData => {
    processIncomingDrop(messageData, ProcessIncomingDropType.DROP_INSERT);
  });
  useWebSocketMessage<WsDropUpdateMessage["data"]>(WsMessageType.DROP_RATING_UPDATE, messageData_0 => {
    processIncomingDrop(messageData_0, ProcessIncomingDropType.DROP_RATING_UPDATE);
  });
  useWebSocketMessage<WsDropUpdateMessage["data"]>(WsMessageType.DROP_REACTION_UPDATE, messageData_1 => {
    processIncomingDrop(messageData_1, ProcessIncomingDropType.DROP_REACTION_UPDATE);
  });

  // Handle tab visibility changes - refresh eligibility when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Mark that tab just became visible, eligibility will be refreshed
        // on the next WebSocket message for any wave
        tabJustBecameVisibleRef.current = true;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Cleanup: Cancel all ongoing fetches on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach(controller_0 => controller_0.abort());
      abortControllersRef.current = {}; // Clear refs
      isFetchingNewestRef.current = {};
      needsRefetchAfterCurrentRef.current = {};
    };
  }, []);

  // No return value needed as this hook works in the background
  return {
    processIncomingDrop,
    processDropRemoved
  };
}